(ns hai.notes.stacks
  "The dashboard scratchpad — a per-user singleton quick note (`:note/scratchpad?
   true`, `:note/source :desktop`). Its text auto-saves; tags, links, and starting
   a fresh note have explicit controls. Starting a fresh note archives the current
   one (clears the scratchpad flag) so it lands in the notes wall, then remounts
   the pad blank. Shared by the desktop dashboard and the mobile quick-note card."
  (:require
   [clojure.string :as str]
   [hai.notes.data :as data]
   [hai.notes.editor :as editor]
   [hai.ui.components.form.core :as form]
   [hai.ui.rf-sugar :refer [>e defevent defevent-fx defsub]]))

(defevent-fx ::load [{:keys [db]} source]
  {:db (-> db
           (assoc ::source source ::loaded? false)
           (dissoc ::note))
   :resolve {:query [{:user/home-scratchpad editor/note-eql}]
             :store-in [::note]
             :transform-fn (fn [resp] (get resp :user/home-scratchpad))
             :on-success-fx [[:dispatch [::loaded]]]}})

(defevent ::loaded [db _]
  (assoc db ::loaded? true))

;; ::load minus the ::loaded? round-trip: re-resolves the note in place while
;; views stay mounted (e.g. after the sharing modal closes).
(defevent-fx ::refresh [_ _]
  {:resolve {:query [{:user/home-scratchpad editor/note-eql}]
             :transform-fn (fn [resp] (get resp :user/home-scratchpad))
             :store-in [::note]}})

(defevent ::clear [db _]
  (dissoc db ::note ::loaded? ::source ::saving? ::generation ::save-request-id))

(defevent-fx ::save
  [{:keys [db]} {:keys [text link-urls tag-uuids tag-names contact-uuids
                        link-note-uuids task-uuids]
                 :as payload}]
  (let [note-uuid (get-in db [::note :note/uuid])
        source (get db ::source :desktop)]
    (cond
      ;; Nothing to create from yet — no text, links, tags, contacts, notes or
      ;; tasks to hold.
      (and (nil? note-uuid) (str/blank? text) (empty? link-urls)
           (empty? tag-uuids) (empty? tag-names) (empty? contact-uuids)
           (empty? link-note-uuids) (empty? task-uuids))
      {:db db}

      :else
      (let [req-id (random-uuid)
            params (cond-> (editor/upsert-params {:note-uuid note-uuid :source source}
                                                 payload)
                     ;; First save creates the active scratchpad; later updates
                     ;; leave the flag untouched.
                     (nil? note-uuid) (assoc :note/scratchpad? true))]
        {:db (assoc db ::saving? true ::save-request-id req-id)
         :resolve {:request-id req-id
                   :query [{(list 'note/upsert params) editor/note-eql}]
                   :transform-fn (fn [resp] (get resp 'note/upsert))
                   :store-in [::note]
                   :on-success-fx [[:dispatch [::save-success]]]
                   :on-error-fx [[:dispatch [::save-error]]]}}))))

;; Optimistic: the link disappears immediately; the response reconciles with
;; server truth via store-in, and an error restores the snapshot.
(defevent-fx ::remove-link
  [{:keys [db]} link-url]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    (let [previous (get-in db [::note :note/activities])]
      {:db (update-in db [::note :note/activities]
                      (fn [activities]
                        (vec (remove #(= link-url (:activity/url %)) activities))))
       :resolve {:query [{(list 'note/remove-link {:note/uuid note-uuid
                                                   :activity/url link-url})
                          [:note/uuid {:note/activities [:activity/uuid :activity/url]}]}]
                 :transform-fn (fn [resp] (get-in resp ['note/remove-link :note/activities]))
                 :store-in [::note :note/activities]
                 :on-error-fx [[:dispatch [::restore-links previous]]
                               [:dispatch [:toast/error {:text "Could not remove link. Try again."}]]]}})))

(defevent ::restore-links [db activities]
  (assoc-in db [::note :note/activities] activities))

;; Optimistic: mirrors ::remove-link for contact links — the chip disappears
;; immediately; an error restores the snapshot.
(defevent-fx ::remove-contact
  [{:keys [db]} contact-uuid]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    (let [previous (get-in db [::note :note/contacts])]
      {:db (update-in db [::note :note/contacts]
                      (fn [contacts]
                        (vec (remove #(= contact-uuid (:contact/uuid %)) contacts))))
       :resolve {:query [{(list 'note/remove-contact {:note/uuid note-uuid
                                                      :contact/uuid contact-uuid})
                          [:note/uuid {:note/contacts [:contact/uuid :contact/full-name :contact/organisation?]}]}]
                 :transform-fn (fn [resp] (get-in resp ['note/remove-contact :note/contacts]))
                 :store-in [::note :note/contacts]
                 :on-error-fx [[:dispatch [::restore-contacts previous]]
                               [:dispatch [:toast/error {:text "Could not remove contact. Try again."}]]]}})))

(defevent ::restore-contacts [db contacts]
  (assoc-in db [::note :note/contacts] contacts))

;; Linked notes and tasks mirror ::remove-contact exactly.
(defevent-fx ::remove-note-link
  [{:keys [db]} linked-uuid]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    (let [previous (get-in db [::note :note/notes])]
      {:db (update-in db [::note :note/notes]
                      (fn [notes]
                        (vec (remove #(= linked-uuid (:note/uuid %)) notes))))
       :resolve {:query [{(list 'note/remove-note {:note/uuid note-uuid
                                                   :note/linked-uuid linked-uuid})
                          [:note/uuid {:note/notes [:note/uuid :note/text]}]}]
                 :transform-fn (fn [resp] (get-in resp ['note/remove-note :note/notes]))
                 :store-in [::note :note/notes]
                 :on-error-fx [[:dispatch [::restore-note-links previous]]
                               [:dispatch [:toast/error {:text "Could not remove linked note. Try again."}]]]}})))

(defevent ::restore-note-links [db notes]
  (assoc-in db [::note :note/notes] notes))

(defevent-fx ::remove-task
  [{:keys [db]} task-uuid]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    (let [previous (get-in db [::note :note/tasks])]
      {:db (update-in db [::note :note/tasks]
                      (fn [tasks]
                        (vec (remove #(= task-uuid (:task/uuid %)) tasks))))
       :resolve {:query [{(list 'note/remove-task {:note/uuid note-uuid
                                                   :task/uuid task-uuid})
                          [:note/uuid {:note/tasks [:task/uuid :task/title]}]}]
                 :transform-fn (fn [resp] (get-in resp ['note/remove-task :note/tasks]))
                 :store-in [::note :note/tasks]
                 :on-error-fx [[:dispatch [::restore-tasks previous]]
                               [:dispatch [:toast/error {:text "Could not remove linked task. Try again."}]]]}})))

(defevent ::restore-tasks [db tasks]
  (assoc-in db [::note :note/tasks] tasks))

(defevent ::save-success [db _]
  (-> db
      (assoc ::saving? false)
      (dissoc ::save-request-id)))

(defevent ::save-error [db _]
  (-> db
      (assoc ::saving? false)
      (dissoc ::save-request-id)))

(defn scratchpad-form-id
  "The scratchpad's single form state, shared by the inline pad and the
   full-screen editor so switching between them hands the live text over
   directly. Keyed by generation: ＋ bumps it, giving the fresh pad a blank
   form."
  [generation]
  [::form generation])

(defevent-fx ::new-note
  [{:keys [db]} _]
  (let [note-uuid (get-in db [::note :note/uuid])
        source (get db ::source :desktop)
        ;; The note entity in db lags the textarea by the autosave debounce plus
        ;; a round-trip; the form state is updated per keystroke, so it has the
        ;; text the user actually sees.
        form-id (scratchpad-form-id (get db ::generation 0))
        text (or (form/->field-current-value db form-id :note/text)
                 (get-in db [::note :note/text])
                 "")]
    (if (and (nil? note-uuid) (str/blank? text))
      ;; Already on a fresh pad — nothing to archive.
      {:db db}
      (let [;; A debounced auto-save may be in flight for this note. Cancel it so
            ;; its response can't re-store the note as the scratchpad after we've
            ;; peeled. We file the same text here, so nothing is lost.
            pending-save-id (::save-request-id db)
            ;; Archive the current note: save its text and clear the scratchpad
            ;; flag. With no note yet it creates one already archived.
            params (cond-> {:note/text text
                            :note/source source
                            :note/scratchpad? false}
                     note-uuid (assoc :note/uuid note-uuid))]
        {:db (cond-> (-> db
                         (assoc ::saving? true)
                         (dissoc ::save-request-id))
               pending-save-id (update :resolve/canceled-requests (fnil conj #{}) pending-save-id))
         :resolve {:query [{(list 'note/upsert params) [:note/uuid]}]
                   :on-success-fx [[:dispatch [::new-note-success]]]
                   :on-error-fx [[:dispatch [::save-error]]]}}))))

;; Peel: forget the archived note as the scratchpad and remount the editor blank
;; (its text atom is seeded once on mount). The note lives on, now in the wall.
(defevent ::new-note-success [db _]
  (-> db
      (dissoc ::note)
      (update ::generation (fnil inc 0))
      (assoc ::saving? false)))

(defevent-fx ::delete
  [{:keys [db]} _]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    ;; Cancel any in-flight autosave so its response can't re-store the note as
    ;; the scratchpad after it's been deleted.
    (let [pending-save-id (::save-request-id db)]
      {:db (cond-> (-> db (assoc ::saving? true) (dissoc ::save-request-id))
             pending-save-id (update :resolve/canceled-requests (fnil conj #{}) pending-save-id))
       :resolve {:query [{(list 'note/delete {:note/uuid note-uuid}) [:note/uuid]}]
                 :on-success-fx [[:dispatch [::delete-success]]]
                 :on-error-fx [[:dispatch [::save-error]]
                               [:dispatch [:toast/error {:text "Could not delete note. Try again."}]]]}})))

;; Forget the deleted note and remount the editor blank (generation bump), the
;; same landing as starting a fresh note — the next keystroke creates a new one.
(defevent ::delete-success [db _]
  (-> db
      (dissoc ::note)
      (update ::generation (fnil inc 0))
      (assoc ::saving? false)))

(defsub ::loaded? [db]
  (get db ::loaded? false))

(defsub ::note [db]
  (get db ::note))

(defsub ::link-urls [db]
  (->> (get-in db [::note :note/activities])
       (keep :activity/url)
       vec))

(defsub ::tags [db]
  (get-in db [::note :note/tags] []))

(defsub ::contacts [db]
  (get-in db [::note :note/contacts] []))

(defsub ::linked-notes [db]
  (get-in db [::note :note/notes] []))

(defsub ::linked-tasks [db]
  (get-in db [::note :note/tasks] []))

(defsub ::saving? [db]
  (get db ::saving? false))

(defsub ::generation [db]
  (get db ::generation 0))

(def ^:private debounce-ms 750)

(def extras-handlers
  "The scratchpad's tag/link save wiring for [[hai.notes.form/note-extras]] —
   hosts merge their display props (`:editing?`, `:tags`, `:link-urls`, …) on
   top."
  {:on-add-tag     (fn [tag] (>e [::save (editor/tag-save-payload tag)]))
   :on-remove-tag  (fn [tag] (>e [::save {:untag-uuids [(:group/uuid tag)]}]))
   :on-add-link    (fn [link-url] (>e [::save {:link-urls [link-url]}]))
   :on-remove-link (fn [link-url] (>e [::remove-link link-url]))
   :on-add-contact    (fn [contact-uuid] (>e [::save {:contact-uuids [contact-uuid]}]))
   :on-remove-contact (fn [contact-uuid] (>e [::remove-contact contact-uuid]))
   :on-add-note       (fn [linked-uuid] (>e [::save {:link-note-uuids [linked-uuid]}]))
   :on-remove-note    (fn [linked-uuid] (>e [::remove-note-link linked-uuid]))
   :on-add-task       (fn [task-uuid] (>e [::save {:task-uuids [task-uuid]}]))
   :on-remove-task    (fn [task-uuid] (>e [::remove-task task-uuid]))
   :on-open-entity    (fn [entity] (>e [::data/open-entity entity]))})

(defn delete-note!
  "Drop the pending autosave so the remount flush can't re-create the note
   being deleted, then delete it."
  [!autosave]
  (when-let [{:keys [discard!]} @!autosave]
    (discard!))
  (>e [::delete]))

(defn autosave
  "Debounced scratchpad save bound to the `text` ratom. Returns the handlers the
   editor wires up: `save!` cancels any pending debounce then upserts the current
   text merged with `extra`; `schedule!` debounces a text-only save; `cancel!`
   clears a pending save (also the unmount cleanup)."
  [text]
  (let [timer   (atom nil)
        cancel! (fn []
                  (when-let [id @timer]
                    (js/clearTimeout id)
                    (reset! timer nil)))
        save!   (fn [extra]
                  (cancel!)
                  (>e [::save (merge {:text @text} extra)]))]
    {:cancel!   cancel!
     :save!     save!
     :flush!    #(save! {})
     :schedule! (fn []
                  (cancel!)
                  (reset! timer (js/setTimeout #(save! {}) debounce-ms)))}))
